import { AuthoredAttachments } from './AuthoredAttachments';
// PostCard.tsx — Feed post card. Assembles AuthorLink / PostMedia / PostStatus /
// PostActions / PostMoreMenu + ReportModal into a single ConsolePanel article.

import { useState } from 'react';
import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PostCardSurface as Article } from './PostCardSurface';
import type { Post } from '@/core/models/post';
import { postPath } from '@/core/lib/postUrl';
import { usePostStatus, useRequireAuth, usePreferences, useSession } from '@/hooks';
import { Icon } from '@/icons';
import { ReportModal } from '@/components/ReportModal';
import { AppealModal } from '@/components/AppealModal';
import { AuthorLink } from './AuthorLink';
import { Coauthors } from './Coauthors';
import { PostMedia } from './PostMedia';
import { PostStatus } from './PostStatus';
import { PostActions } from './PostActions';
import { DiscussionBell } from '@/features/discussions/DiscussionBell';
import { PostMoreMenu } from './PostMoreMenu';
import { EditPostDialog } from './EditPostDialog';
import { DeletePostDialog } from './DeletePostDialog';
import { DeletedTombstone } from './DeletedTombstone';
import { useOwnPostActions } from './useOwnPostActions';
import { PostBody } from './PostBody';
import { PostPoll } from './PostPoll';
import { AudienceBadge, ScheduledLabel, DraftLabel } from './AudienceBadge';
import { QuoteEmbed } from './QuoteEmbed';
import { ClipEmbed } from './ClipEmbed';
import { LinkPreviewCard, linkPreviewToShow } from './LinkPreviewCard';
import { ThreadChip } from './ThreadChip';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PostCardProps {
  post: Post;
  /** Optional feed index (reserved for future animation staggering). */
  index?: number;
  /**
   * True only when this card is being rendered as part of the For-You feed
   * list (HomePage's `tab === 'for-you'`). This is the actual render gate for
   * "Why am I seeing this?" and the always-visible reason line — NOT
   * `post.forYouReasons`' mere presence (2026-08-10 adversarial review,
   * finding F1). `forYouReasons` is written into the SHARED post store by a
   * for-you fetch (postsService.listFeed merges rather than replaces), and
   * `upsertPost`'s shallow merge can never clear that key once set — so the
   * very same post object keeps carrying stale reasons after the viewer
   * switches to Following/Trending/Discover, or after a poll/like/edit
   * refresh re-merges the SAME post from a non-for-you endpoint. Without this
   * explicit prop, a post the viewer saw on For You would keep showing "You
   * follow @x" on every other tab forever.
   */
  forYouContext?: boolean;
  /**
   * Following feed only: who put this post in the viewer's feed by spreading it
   * (Spread-It!). Passed by HomePage from feedStore, never read off the post itself:
   * the same post object is shared with every other tab, where the label is wrong.
   */
  spreadBy?: NonNullable<Post['spreadBy']>;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

/* "Jordan Vale spread this": the reason a post by someone you don't follow is here. */
const SpreadLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  a {
    color: inherit;
    font-weight: 600;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const AuthorSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const OwnerRemovedTombstone = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: ${({ theme }) => `${theme.color.danger}24`};
  color: ${({ theme }) => theme.color.danger};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const TombstoneText = styled.span`
  color: ${({ theme }) => theme.color.textMuted};
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ReasonLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

// ─── Component ────────────────────────────────────────────────────────────────

export function PostCard({ post, index, forYouContext = false, spreadBy }: PostCardProps) {
  const status = usePostStatus(post.id);
  const requireAuth = useRequireAuth();
  const navigate = useNavigate();
  const { prefs } = usePreferences();
  const { currentUser } = useSession();
  const [reporting, setReporting] = useState(false);
  const [appealingCaseId, setAppealingCaseId] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const own = useOwnPostActions(post);

  const councilRemoved = status?.decision?.outcome === 'removed';
  // Owner soft-removal supersedes body display independently of council status.
  const ownerRemoved = post.removed === true;
  const removed = councilRemoved || ownerRemoved;
  const authorDeleted = post.deleted === true;
  // A scheduled draft (published:false) is only ever visible to its own author —
  // interactions go dead via the same DisabledHint idiom as a deleted post, since
  // the server 409s every interaction on an unpublished post.
  const scheduled = post.published === false;
  const disabledReason: 'deleted' | 'scheduled' | undefined = authorDeleted
    ? 'deleted'
    : scheduled
      ? 'scheduled'
      : undefined;
  const linkPreview = linkPreviewToShow(post);

  if (hidden) return null;

  function onCardClick(e: MouseEvent<HTMLElement>) {
    // Don't hijack interaction with focusable / interactive descendants.
    // Note: [role="dialog"] is intentionally absent — the ReportModal is rendered
    // via a portal so its DOM node is never a descendant of this article element.
    // `video`: its native controls (play, scrub, volume) must never open the post.
    if ((e.target as HTMLElement).closest('a, button, video, [role="menu"], .post-status')) {
      return;
    }
    navigate(postPath(post));
  }

  function onReport() {
    if (!requireAuth('report')) return;
    setReporting(true);
  }

  function onAppeal(caseId: string) {
    if (!requireAuth('appeal')) return;
    // The server requires a written reason — collect it before POSTing.
    setAppealingCaseId(caseId);
  }

  return (
    <Article
      aria-label={
        post.removed
          ? `Removed post by ${post.name}`
          : `Post by ${post.name}: ${post.body.slice(0, 80)}`
      }
      data-post-id={post.id}
      data-density={prefs.feedDensity}
      $compact={prefs.feedDensity === 'compact'}
      onClick={onCardClick}
    >
      {spreadBy && (
        <SpreadLine>
          <Icon name="spread-it" size={14} />
          {spreadBy.self ? (
            <span>You spread this</span>
          ) : (
            <span>
              <Link to={`/profile/${spreadBy.handle.replace(/^@/, '')}`}>{spreadBy.name}</Link>{' '}
              spread this
            </span>
          )}
        </SpreadLine>
      )}
      {/* Top row: author + ··· menu */}
      <TopRow>
        <AuthorSlot>
          <AuthorLink post={post} />
        </AuthorSlot>
        {!post.deleted && !post.removed && <DiscussionBell postId={post.id} />}
        <PostMoreMenu
          post={post}
          isOwn={own.isOwn}
          onReport={onReport}
          onEdit={own.openEdit}
          onDelete={own.openDelete}
          onHide={() => setHidden(true)}
          onUnhide={() => setHidden(false)}
          forYouContext={forYouContext}
        />
      </TopRow>

      {/* Feature 4 (For-You transparency): opt-in always-visible reason line —
          gated on forYouContext (F1), not merely post.forYouReasons' presence
          — see PostCardProps.forYouContext's docstring — AND only rendered
          when the viewer turned the setting on in Settings → Content Preferences.
          Filter out any reason with an empty label (F12) so a broken/blank
          reason never renders a bare icon with nothing next to it. */}
      {forYouContext &&
        currentUser?.showFeedReasons &&
        post.forYouReasons &&
        post.forYouReasons.filter((r) => r.label).length > 0 && (
          <ReasonLine>
            <Icon name="info" size={12} />
            <span>{post.forYouReasons.filter((r) => r.label)[0]?.label}</span>
          </ReasonLine>
        )}

      {/* Deleted-by-author tombstone — body stays visible, interactions go dead */}
      {authorDeleted && <DeletedTombstone />}

      {/* Scheduled draft + non-default audience badges — author-only content, but the
          payload already scopes what each viewer receives, so this just renders it. */}
      {(scheduled || (post.audience && post.audience.type !== 'everyone')) && (
        <BadgeRow>
          {scheduled && post.scheduledAt && <ScheduledLabel scheduledAt={post.scheduledAt} />}
          {scheduled && !post.scheduledAt && <DraftLabel />}
          {post.audience && <AudienceBadge audience={post.audience} />}
        </BadgeRow>
      )}

      {/* Body + media — hidden when removed */}
      {!removed && (
        <>
          <PostBody body={post.body} />
          <AuthoredAttachments
            attachments={post.attachments}
            perspectives={post.perspectives ?? undefined}
          />
          <Coauthors post={post} />
          {/* Poll and images are mutually exclusive — render whichever is present.
              `post` unlocks the multi-image array; the first card in the feed loads eagerly. */}
          {post.poll ? (
            <PostPoll postId={post.id} poll={post.poll} />
          ) : (
            <PostMedia post={post} media={post.media} priority={index === 0} />
          )}
          {post.quotedPost && <QuoteEmbed quotedPost={post.quotedPost} />}
          {post.clip && <ClipEmbed clip={post.clip} postId={post.id} />}
          {/* 1.24.00 C1: at most one card under the body — see linkPreviewToShow. */}
          {linkPreview && <LinkPreviewCard preview={linkPreview} />}
          {/* 1.24.00 C2: parts carry a numbered chip back to the thread root. */}
          {post.thread && <ThreadChip thread={post.thread} />}
        </>
      )}

      {/* Owner-removed tombstone — shown when the owner has soft-removed the post */}
      {ownerRemoved && (
        <OwnerRemovedTombstone aria-label="Removed by moderator">
          <Icon name="ban" size={16} />
          <TombstoneText>
            Removed by moderator
            {post.removedReason ? `: ${post.removedReason}` : ''}
          </TombstoneText>
        </OwnerRemovedTombstone>
      )}

      {/* Action row */}
      <PostActions post={post} disabledReason={disabledReason} />

      {/* Moderation status banner */}
      <PostStatus status={status} onAppeal={onAppeal} />

      {/* Report modal — conditionally mounted */}
      {reporting && (
        <ReportModal target={{ kind: 'post', post }} onClose={() => setReporting(false)} />
      )}

      {/* Appeal modal — collects the required reason */}
      {appealingCaseId && (
        <AppealModal caseId={appealingCaseId} onClose={() => setAppealingCaseId(null)} />
      )}

      {/* Edit dialog — own post only */}
      {own.editing && (
        <EditPostDialog
          post={post}
          onClose={own.closeEdit}
          onSave={own.saveEdit}
          editWindowAdvisory={own.editWindowAdvisory}
        />
      )}

      {/* Delete confirm — own post only */}
      <DeletePostDialog
        open={own.deleting}
        onCancel={own.closeDelete}
        onConfirm={() => void own.confirmDelete()}
      />
    </Article>
  );
}
